import React, { useCallback } from "react";
import { useRecoilState, useSetRecoilState } from "recoil";
import { useFormik } from "formik";
import * as Yup from "yup";
// material
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Zoom,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
// __apis__
import { registerWaterInTake } from "src/__apis__/waterInTakeTracking";
// atoms
import registerWaterInTakePopUpAtom from "src/recoil/atoms/registerWaterInTakePopUpAtom";
import alertAtom from "src/recoil/atoms/alertAtom";
// hooks
import useWaterInTakeTracking from "src/hooks/useWaterInTakeTracking";
// components
import Iconify from "./Iconify";

// ------------------------------------------------------------------------------------------------------------------------

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom timeout={5} ref={ref} {...props} />;
});

// ------------------------------------------------------------------------------------------------------------------------

function RegisterWaterInTakePopUp() {
  const [waterInTake, waterInTakeTrackingFetcher] = useWaterInTakeTracking();

  const [registerWaterInTakePopUp, triggerRegisterWaterInTakePopUp] =
    useRecoilState(registerWaterInTakePopUpAtom);

  const triggerAlert = useSetRecoilState(alertAtom);

  const formik = useFormik({
    initialValues: {
      size: 0,
      unit: "",
    },
    validationSchema: Yup.object().shape({
      size: Yup.number()
        .min(0, "Water in take cannot be negative value")
        .required("Water in take is required"),

      unit: Yup.string().required("Unit must be selected"),
    }),
    onSubmit: async (values, { resetForm }) => {
      await registerWaterInTake(calcWaterInTakeLitres())
        .then(async (response) => {
          triggerAlert({
            triggered: true,
            type: "success",
            message: "Water In-Take Registered Successfully",
          });
          waterInTakeTrackingFetcher();
          resetForm();
        })
        .catch((error) => {
          console.log("Error registereing water in-take", error);
          triggerAlert({
            triggered: true,
            type: "error",
            message: "Error registering water in-take",
          });
        });
    },
  });

  const onCloseHandler = () => {
    triggerRegisterWaterInTakePopUp(false);
  };

  const {
    errors,
    dirty,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
    values,
  } = formik;

  const calcWaterInTakeLitres = useCallback(() => {
    let waterInTake = 0;
    if (values.unit === "cup") {
      waterInTake = 0.24 * values.size;
    } else if (values.unit === "small-bottle") {
      waterInTake = 0.6 * values.size;
    } else if (values.unit === "large-bottle") {
      waterInTake = 1.5 * values.size;
    } else if (values.unit === "millilitres") {
      waterInTake = values.size / 1000;
    } else if (values.unit === "litres") {
      waterInTake = values.size;
    }

    return waterInTake;
  }, [values]);

  return (
    <Dialog
      TransitionComponent={Transition}
      open={registerWaterInTakePopUp}
      onClose={onCloseHandler}
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            component="img"
            src="/static/icons/water-bottle.png"
            sx={{ width: 50, height: 50, mr: 2 }}
          />
          <Typography variant="h4">Register Your Water In-Take</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mt={4}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <TextField
                label="Size"
                type="number"
                value={values.size}
                onChange={(event) => setFieldValue("size", event.target.value)}
                {...getFieldProps("size")}
                error={Boolean(touched.size && errors.size)}
                helperText={touched.size && errors.size}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Unit"
                value={values.unit}
                onChange={(event) => setFieldValue("unit", event.target.value)}
                {...getFieldProps("unit")}
                error={Boolean(touched.unit && errors.unit)}
                helperText={touched.unit && errors.unit}
                fullWidth
                select
              >
                <MenuItem value="cup">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Iconify
                      icon="material-symbols:glass-cup"
                      sx={{ width: 25, height: 25 }}
                    />
                    <Typography variant="subtitle1" sx={{ ml: 1 }}>
                      Cup
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="small-bottle">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Iconify
                      icon="material-symbols:water-bottle-outline"
                      sx={{ width: 25, height: 25 }}
                    />
                    <Typography variant="subtitle1" sx={{ ml: 1 }}>
                      Small Bottle
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="large-bottle">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Iconify
                      icon="material-symbols:water-bottle-large-outline"
                      sx={{ width: 25, height: 25 }}
                    />
                    <Typography variant="subtitle1" sx={{ ml: 1 }}>
                      Large Bottle
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="litres">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Iconify
                      icon="fa6-solid:glass-water-droplet"
                      sx={{ width: 25, height: 25 }}
                    />
                    <Typography variant="subtitle1" sx={{ ml: 1 }}>
                      Litres
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value="millilitres">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Iconify
                      icon="material-symbols:water-drop-outline"
                      sx={{ width: 25, height: 25 }}
                    />
                    <Typography variant="subtitle1" sx={{ ml: 1 }}>
                      MilliLitres
                    </Typography>
                  </Box>
                </MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="error" onClick={onCloseHandler}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSubmit}
          loading={isSubmitting}
          disabled={!dirty}
          endIcon={<Iconify icon="ic:outline-send" />}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

export default RegisterWaterInTakePopUp;
